import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

export interface CategoryRatioDatum {
  /** Stable category id (string form). */
  id: string;
  /** Display name. */
  label: string;
  /** Owner-share ratio as a percentage (0–100). */
  percent: number;
}

interface CategoryRatioTableProps {
  data: CategoryRatioDatum[];
  loading?: boolean;
}

const fmtPercent = (p: number) => `${p.toFixed(2)}%`;

/**
 * Per-category visibility table: Category name + owner-share "Visibility
 * score", sorted high→low by the caller. Mirrors the AI-model block but in
 * tabular form since the category list is long.
 */
export function CategoryRatioTable({ data, loading }: CategoryRatioTableProps) {
  if (loading && data.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (data.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          No data for the selected filters.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined">
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {data.length} {data.length === 1 ? 'item' : 'items'}
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell align="right" sx={{ width: 160 }}>
                Visibility score
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((d) => (
              <TableRow key={d.id} hover>
                <TableCell>{d.label}</TableCell>
                <TableCell align="right">{fmtPercent(d.percent)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
